import { call } from '@rental/contracts/client'
import { format } from '@rental/i18n'
import { Notice } from '@rental/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { can } from '../access'
import { useAccess } from '../access-context'
import { api, load } from '../api'
import { OutcomeNotice } from '../components/OutcomeNotice'
import { PageHeader } from '../components/PageHeader'
import { Guard, LoadState } from '../components/states'
import { useLocale } from '../i18n'
import { problemOf } from '../problems'
import { TermsEditor } from '../terms/TermsEditor'
import { VersionList } from '../terms/VersionList'

export const TERMS_QUERY_KEY = ['terms'] as const

type Outcome = { kind: 'saved' | 'activated'; version: string }

/**
 * OPS-1, Regole del contratto: the Owner's screen for the rental terms. The
 * editor on the left starts from the version in force (or any opened from the
 * list) and saves a new version; the list on the right holds every version,
 * and puts one in force with a reason and the Owner's authenticator code. A
 * contract already prepared or signed keeps its own text by construction (the
 * snapshot at the turn of the iPad, CTR-1).
 */
export function TermsScreen() {
  const { messages: m } = useLocale()
  const { access } = useAccess()
  const allowed = can(access, 'terms.manage')
  const copy = m.terms
  const queryClient = useQueryClient()
  const terms = useQuery({
    queryKey: TERMS_QUERY_KEY,
    queryFn: () => load(call(api.GET('/v1/staff/terms'))),
    enabled: allowed,
  })
  // The version the editor starts from: chosen here, else the one in force, else the newest.
  const [opened, setOpened] = useState<string | null>(null)
  const [outcome, setOutcome] = useState<Outcome | null>(null)
  const startFrom = opened ?? terms.data?.current ?? terms.data?.versions[0]?.version ?? null
  const text = useQuery({
    queryKey: [...TERMS_QUERY_KEY, 'version', startFrom],
    queryFn: () =>
      load(
        call(
          api.GET('/v1/staff/terms/versions/{version}', {
            params: { path: { version: startFrom ?? '' } },
          }),
        ),
      ),
    enabled: allowed && startFrom !== null,
  })
  const refresh = () => queryClient.invalidateQueries({ queryKey: TERMS_QUERY_KEY })

  return (
    <Guard allowed={allowed} title={copy.title}>
      <PageHeader title={copy.title} intro={copy.intro} />
      {outcome ? (
        <OutcomeNotice key={`${outcome.kind}-${outcome.version}`}>
          <p>
            {outcome.kind === 'saved'
              ? format(copy.saved, { version: outcome.version })
              : format(copy.activated, { version: outcome.version })}
          </p>
        </OutcomeNotice>
      ) : null}
      <LoadState data={terms.data} problem={problemOf(terms.error)}>
        {(list) => (
          <>
            {list.current === null ? (
              <Notice tone="warning">
                <p>{copy.none}</p>
              </Notice>
            ) : null}
            <div className="grid gap-6 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)] lg:items-start">
              {startFrom === null ? (
                <TermsEditor
                  key="none"
                  source={null}
                  onSaved={(version) => {
                    setOutcome({ kind: 'saved', version })
                    setOpened(version)
                    void refresh()
                  }}
                />
              ) : (
                <LoadState data={text.data} problem={problemOf(text.error)}>
                  {(source) => (
                    <TermsEditor
                      key={source.version}
                      source={source}
                      onSaved={(version) => {
                        setOutcome({ kind: 'saved', version })
                        setOpened(version)
                        void refresh()
                      }}
                    />
                  )}
                </LoadState>
              )}
              <VersionList
                terms={list}
                opened={startFrom}
                onOpen={(version) => {
                  setOpened(version)
                  setOutcome(null)
                }}
                onActivated={(version) => {
                  setOutcome({ kind: 'activated', version })
                  void refresh()
                }}
              />
            </div>
          </>
        )}
      </LoadState>
    </Guard>
  )
}
